<template>
    <div>
        <div
            v-if="videoId"
            data-test="video-attached"
            class="relative mt-2 w-24"
        >
            <span class="sr-only">The video you added to this review</span>

            <div
                class="aspect-w-1 aspect-h-1 overflow-hidden rounded-md border border-gray-200 bg-gray-100"
            >
                <div>
                    <!-- The #t fragment is what puts a frame on screen: iOS Safari will not decode
                         a video it has not been asked to play, and draws an empty box in its
                         place, but it does honour a request for the moment the fragment names. The
                         URL the tile is revoked by stays whole, so the fragment is added here and
                         not stored. -->
                    <video
                        v-if="thumbnailUrl"
                        data-test="video-thumbnail"
                        :src="`${thumbnailUrl}#t=${FIRST_FRAME_SECONDS}`"
                        muted
                        playsinline
                        preload="metadata"
                        class="h-full w-full object-cover"
                    />
                    <div
                        v-else
                        data-test="video-thumbnail-pending"
                        class="flex h-full w-full items-center justify-center text-gray-400"
                    >
                        <VideoCameraIcon class="h-8 w-8" aria-hidden="true" />
                    </div>
                </div>
            </div>

            <button
                type="button"
                data-test="remove-video"
                class="absolute -right-2 -top-2 rounded-full bg-white text-gray-400 shadow hover:text-gray-600 focus:outline-none"
                @click="removeTheVideo"
            >
                <span class="sr-only">Remove this video</span>
                <XCircleIcon class="h-6 w-6" aria-hidden="true" />
            </button>
        </div>

        <div v-else-if="uploading" class="mt-3">
            <p class="text-sm text-gray-500">Sending your video…</p>
            <div
                class="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-200"
            >
                <div
                    data-test="video-progress"
                    class="h-1 bg-nutreance-green"
                    :style="{ width: `${progress}%` }"
                />
            </div>
        </div>

        <p
            v-else-if="!allowed"
            data-test="video-limit"
            class="mt-2 text-sm text-gray-500"
        >
            You have already added a video to this review.
        </p>

        <p
            v-if="error"
            data-test="video-failed"
            class="mt-2 text-sm text-red-600"
        >
            {{ error }}
        </p>

        <ReviewDialog
            ref="dialog"
            :open="recorderOpen"
            panel-class="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-4 text-left shadow-xl transition-all sm:p-6"
            @close="closeRecorder"
        >
            <div ref="frame" class="relative mx-auto" :style="frameCap">
                <div
                    class="aspect-w-9 aspect-h-16 sm:aspect-w-16 sm:aspect-h-9"
                    :style="frameShape"
                >
                    <!-- The classes are the frame's shape until the camera or the
                         take reports one of its own, which frameShape then
                         overrides. The aspect box stretches every direct child to
                         fill the frame, so it is given exactly one and the videos
                         sit inside it. A node injected beside a <video>, as a
                         playback-speed extension injects one, would otherwise be
                         stretched over the clip, where it swallows every click
                         meant for the controls. -->
                    <div>
                        <!-- playsinline is not optional: without it iOS takes the
                             self-view fullscreen and the controls disappear. muted
                             is not either, or the customer hears themselves
                             through the speakers and the microphone picks it
                             straight back up. The mirroring is the self-view alone
                             — the recording is taken from the stream, which the
                             CSS transform never touches. -->
                        <video
                            v-show="stage !== 'review'"
                            ref="preview"
                            data-test="video-preview"
                            autoplay
                            playsinline
                            muted
                            class="h-full w-full rounded-md bg-black object-contain"
                            style="transform: scaleX(-1)"
                            @loadedmetadata="fitTheFrameToTheMedia"
                            @resize="fitTheFrameToTheMedia"
                        />
                        <!-- v-if and not v-show, unlike the self-view above: this
                             one is filled from a src, and an element standing by
                             on an empty one resolves it against the page and sits
                             in a media error until a take replaces it. -->
                        <video
                            v-if="stage === 'review'"
                            data-test="video-playback"
                            :src="playbackUrl"
                            controls
                            playsinline
                            class="h-full w-full rounded-md bg-black object-contain"
                            @loadedmetadata="showTheTake"
                        />
                    </div>
                </div>

                <div
                    v-if="stage === 'countdown'"
                    class="absolute inset-0 flex items-center justify-center"
                >
                    <span
                        data-test="video-countdown"
                        class="text-7xl font-bold text-white"
                        >{{ countdown }}</span
                    >
                </div>

                <div
                    v-else-if="stage === 'recording'"
                    class="absolute inset-x-0 bottom-0 bg-black bg-opacity-60 p-3 text-white"
                >
                    <div
                        class="h-1 w-full overflow-hidden rounded-full bg-white bg-opacity-30"
                    >
                        <div
                            data-test="video-take-progress"
                            class="h-1 bg-nutreance-green"
                            :style="{
                                width: `${takeProgress}%`,
                            }"
                        />
                    </div>
                    <p
                        data-test="video-remaining"
                        class="mt-1 text-xs"
                        :class="
                            remaining <= ENDING_SOON_SECONDS
                                ? 'font-semibold text-amber-300'
                                : ''
                        "
                    >
                        {{ remaining }} seconds left
                    </p>
                </div>
            </div>

            <div class="mt-4 flex justify-center gap-3">
                <button
                    type="button"
                    data-test="close-recorder"
                    class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                    @click="closeRecorder"
                >
                    Cancel
                </button>

                <button
                    v-if="stage === 'ready'"
                    type="button"
                    data-test="start-recording"
                    :disabled="openingCamera"
                    class="inline-flex items-center rounded-md border border-transparent bg-nutreance-green px-4 py-2 text-sm font-medium text-white hover:bg-nutreance-green-darker focus:outline-none"
                    @click="startCountdown"
                >
                    <CameraIcon class="mr-2 h-5 w-5" aria-hidden="true" />
                    Record
                </button>

                <button
                    v-if="stage === 'recording'"
                    type="button"
                    data-test="stop-recording"
                    :disabled="finishing"
                    class="inline-flex items-center rounded-md border border-transparent bg-nutreance-green px-4 py-2 text-sm font-medium text-white hover:bg-nutreance-green-darker focus:outline-none"
                    @click="finishRecording"
                >
                    <StopIcon class="mr-2 h-5 w-5" aria-hidden="true" />
                    Stop Recording
                </button>

                <button
                    v-if="stage === 'review'"
                    type="button"
                    data-test="retake-video"
                    class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                    @click="retake"
                >
                    <VideoCameraIcon class="mr-2 h-5 w-5" aria-hidden="true" />
                    Retake
                </button>

                <button
                    v-if="stage === 'review'"
                    type="button"
                    data-test="save-video"
                    class="inline-flex items-center rounded-md border border-transparent bg-nutreance-green px-4 py-2 text-sm font-medium text-white hover:bg-nutreance-green-darker focus:outline-none"
                    @click="saveTheTake"
                >
                    <CheckIcon class="mr-2 h-5 w-5" aria-hidden="true" />
                    Save
                </button>
            </div>
        </ReviewDialog>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import {
    CameraIcon,
    CheckIcon,
    StopIcon,
    VideoCameraIcon,
    XCircleIcon,
} from "@heroicons/vue/solid";
import {
    requestReviewVideoUploadUrl,
    uploadReviewMedia,
} from "../../api/reviews";
import { useMediaFrame } from "../../functions/useMediaFrame";
import { useMediaRecorder } from "../../functions/useMediaRecorder";
import ReviewDialog from "./ReviewDialog.vue";

type Stage = "ready" | "countdown" | "recording" | "review";

const COUNTDOWN_FROM = 3;
const TICK_MS = 100;
const ENDING_SOON_SECONDS = 10;
const METADATA_TIMEOUT_MS = 10000;
const FIRST_FRAME_SECONDS = 0.1;

const props = withDefaults(
    defineProps<{
        allowed?: boolean;
        clipSeconds?: number;
        csrf?: string;
        initialId?: string;
        maximumBytes?: number;
    }>(),
    {
        allowed: true,
        clipSeconds: 60,
        csrf: "",
        initialId: "",
        maximumBytes: 190 * 1024 * 1024,
    },
);

const emit = defineEmits<{
    (event: "changed", id: string | null): void;
    (event: "atCapacity", atCapacity: boolean): void;
    (event: "uploading", uploading: boolean): void;
}>();

const recorder = useMediaRecorder();

const dialog = ref<InstanceType<typeof ReviewDialog> | null>(null);
const panel = computed<HTMLElement | null>(() => dialog.value?.panel ?? null);

const { frame, frameShape, frameCap, fitTheFrameToTheMedia, resetTheFrame } =
    useMediaFrame(panel);

const videoId = ref<string | null>(props.initialId || null);
const uploading = ref(false);
const progress = ref(0);
const error = ref("");
const thumbnailUrl = ref("");

const preview = ref<HTMLVideoElement | null>(null);
const openingCamera = ref(false);
const recorderOpen = ref(false);
const stage = ref<Stage>("ready");
const countdown = ref(COUNTDOWN_FROM);
const elapsed = ref(0);
const playbackUrl = ref("");

let liveStream: MediaStream | null = null;
let takenClip: Blob | null = null;
let ticker: number | null = null;
let takeStartedAt = 0;
// Bumped by every close, so a camera still being granted when the dialog shuts can tell that its
// stream is no longer wanted.
let cameraGeneration = 0;

const finishing = ref(false);

/**
 * Whether another clip can be taken right now — the question a full photo strip answers too, so
 * the section can hold both controls back on the same terms.
 */
const atCapacity = computed(
    () => !props.allowed || videoId.value !== null || uploading.value,
);
const takeProgress = computed(() =>
    props.clipSeconds > 0
        ? Math.min(100, Math.round((elapsed.value / props.clipSeconds) * 100))
        : 0,
);
const remaining = computed(() =>
    Math.max(0, Math.ceil(props.clipSeconds - elapsed.value)),
);

/**
 * Tell the section which id to post, whether the upload is still running, and whether the review
 * can still take a clip.
 */
function announce(): void {
    emit("changed", videoId.value);
    emit("uploading", uploading.value);
    emit("atCapacity", atCapacity.value);
}

function beginUploading(): void {
    uploading.value = true;
    progress.value = 0;
    error.value = "";

    announce();
}

function endUploading(): void {
    uploading.value = false;

    announce();
}

function removeTheVideo(): void {
    videoId.value = null;
    error.value = "";

    releaseTheThumbnail();
    announce();
}

/**
 * Show the clip's own first frame on the tile.
 *
 * Only a clip picked or recorded here can offer one: a clip restored by a bounced submission left
 * its bytes behind with the redirect, and Cloudflare has no still of its own until it has finished
 * transcoding.
 */
function showTheFirstFrameOf(clip: Blob): void {
    releaseTheThumbnail();

    thumbnailUrl.value = URL.createObjectURL(clip);
}

function releaseTheThumbnail(): void {
    if (thumbnailUrl.value) {
        URL.revokeObjectURL(thumbnailUrl.value);
        thumbnailUrl.value = "";
    }
}

/**
 * Add the clip a selection carried, which is one or none.
 *
 * A selection carrying two clips is refused rather than trimmed to the first: a customer who
 * cannot tell which one was kept has silently lost the other.
 */
async function addFiles(files: File[]): Promise<void> {
    if (files.length === 0) {
        return;
    }

    if (files.length > 1 || atCapacity.value) {
        error.value = "Only one video can be added to a review.";

        return;
    }

    // Before the asynchronous checks below rather than after them: a submission landing in that
    // gap would navigate away from a file the customer had already handed over.
    beginUploading();

    const refusal = await reasonItCannotBeUploaded(files[0]);

    if (refusal !== null) {
        error.value = refusal;
        endUploading();

        return;
    }

    await sendToCloudflare(files[0], files[0].name);
}

/**
 * Why a picked file cannot be uploaded, or null when it can.
 *
 * A picked file can run to any length or size — both knowable here, before the mint that would
 * reserve paid Stream storage for it.
 */
async function reasonItCannotBeUploaded(file: File): Promise<string | null> {
    const tooLarge = reasonItIsTooLarge(file.size);

    if (tooLarge !== null) {
        return tooLarge;
    }

    const clip = await probe(file);

    if (clip.duration > props.clipSeconds) {
        return `Please keep it under ${props.clipSeconds} seconds — this one is ${Math.round(clip.duration)}.`;
    }

    // The track and not the duration is what says this is a video: a media element reports a
    // duration for an audio file too, and one minted as a clip is billed, uploaded, and then either
    // refused by Stream or attached as something no player can show. A video whose header carries
    // no duration is still a video — Android hands back exactly that — so it passes here and is
    // bounded by the mint's own reservation instead.
    if (!clip.hasVideo) {
        return "That file could not be read as a video. Please try recording again.";
    }

    return null;
}

function reasonItIsTooLarge(bytes: number): string | null {
    return bytes > props.maximumBytes
        ? "That video is too large to upload. Please record a shorter one."
        : null;
}

/**
 * How long the file runs, in seconds, or 0 when the browser cannot tell.
 *
 * A metadata-only load: the browser reads the file's header, not the file.
 */
function probe(file: File): Promise<{ duration: number; hasVideo: boolean }> {
    return new Promise((resolve) => {
        const element = document.createElement("video");
        const objectUrl = URL.createObjectURL(file);
        let giveUp = 0;

        const settle = (duration: number, hasVideo: boolean): void => {
            window.clearTimeout(giveUp);
            URL.revokeObjectURL(objectUrl);
            resolve({ duration, hasVideo });
        };

        // Bounded, because a probe that answers neither way would hold the upload guard down for
        // good: Save would stay disabled with nothing on screen to explain it, and the customer
        // could no longer post the review they had already written.
        giveUp = window.setTimeout(() => settle(0, false), METADATA_TIMEOUT_MS);

        element.preload = "metadata";
        // A duration of Infinity is normal for a live-recorded container and must not read as
        // over-long, so it is reported as zero and only the track decides.
        element.onloadedmetadata = () =>
            settle(
                Number.isFinite(element.duration) ? element.duration : 0,
                element.videoWidth > 0,
            );
        element.onerror = () => settle(0, false);
        element.src = objectUrl;
    });
}

/**
 * Mint a one-time URL and send the clip to it, holding Save down until it lands.
 */
async function sendToCloudflare(blob: Blob, filename: string): Promise<void> {
    try {
        const upload = await requestReviewVideoUploadUrl(props.csrf);

        await uploadReviewMedia(upload.uploadUrl, blob, filename, (percent) => {
            progress.value = percent;
        });

        videoId.value = upload.id;

        showTheFirstFrameOf(blob);
    } catch (thrown) {
        error.value =
            thrown instanceof Error
                ? thrown.message
                : "The upload did not finish. Please try again.";
    } finally {
        endUploading();
    }
}

/**
 * Show the self-view, and report whether in-page recording was on offer at all.
 *
 * False means this customer has no recorder here — the browser has no MediaRecorder, or the camera
 * was declined, which is not an error and nothing is said about it.
 */
async function openRecorder(): Promise<boolean> {
    if (!recorder.isSupported()) {
        return false;
    }

    // False here would open the phone's camera on top of the recorder already coming.
    if (openingCamera.value || recorder.isOpen()) {
        return true;
    }

    if (!(await acquireTheCamera())) {
        return false;
    }

    resetTheTake();
    recorderOpen.value = true;

    await nextTick();

    showTheLivePreview();

    return true;
}

/**
 * Ask for the camera, and report whether the stream that arrived is still wanted.
 *
 * One request at a time, because a second while the permission prompt is up resolves into a second
 * stream and overwrites the reference to the first, whose camera light then stays on with nothing
 * able to reach its tracks. The generation check closes the same hole in time rather than in count:
 * the dialog can be closed while the prompt is still up, and the stream would then land after
 * closeRecorder() had already released everything it knew about.
 */
async function acquireTheCamera(): Promise<boolean> {
    if (openingCamera.value || recorder.isOpen()) {
        return false;
    }

    const generation = cameraGeneration;

    openingCamera.value = true;

    try {
        liveStream = await recorder.open();
    } catch {
        return false;
    } finally {
        openingCamera.value = false;
    }

    if (generation !== cameraGeneration) {
        releaseTheCamera();

        return false;
    }

    return true;
}

function showTheLivePreview(): void {
    if (!preview.value) {
        // The dialog never mounted its video, so nothing would ever stop the tracks.
        closeRecorder();

        return;
    }

    preview.value.srcObject = liveStream;
}

function resetTheTake(): void {
    stage.value = "ready";
    countdown.value = COUNTDOWN_FROM;
    elapsed.value = 0;

    discardTheTake();
}

function discardTheTake(): void {
    takenClip = null;

    if (playbackUrl.value) {
        URL.revokeObjectURL(playbackUrl.value);
        playbackUrl.value = "";
    }
}

/**
 * Count in over the live preview: a recording that starts on the click begins mid-flinch. No audio
 * cue, because a chime would land in the recording.
 */
function startCountdown(): void {
    stage.value = "countdown";
    countdown.value = COUNTDOWN_FROM;

    runTimer(1000, () => {
        countdown.value--;

        if (countdown.value <= 0) {
            beginRecording();
        }
    });
}

function beginRecording(): void {
    try {
        recorder.start();
    } catch {
        error.value = "This browser could not start recording.";
        closeRecorder();

        return;
    }

    stage.value = "recording";
    takeStartedAt = performance.now();
    elapsed.value = 0;

    runTimer(TICK_MS, advanceRecordingClock);
    document.addEventListener("visibilitychange", endTheTakeIfHidden);
}

/**
 * End the take when the page goes away.
 *
 * A hidden page can stop running timers altogether while MediaRecorder keeps going, and the clock
 * would then only notice on return — by which point the clip is longer than the mint reserved and
 * Cloudflare will refuse it after the customer has uploaded the whole thing. Nothing worth keeping
 * is recorded while the page is not on screen anyway.
 */
function endTheTakeIfHidden(): void {
    if (document.hidden && stage.value === "recording") {
        finishRecording();
    }
}

/**
 * Read the clock and stop the take at the configured clip length.
 *
 * Elapsed time is measured from the take's start rather than accumulated a tick at a time: a
 * backgrounded tab throttles its timers to a fraction of their rate while MediaRecorder keeps
 * running, so counting callbacks would let a nominal sixty seconds record for minutes — past the
 * duration the mint reserved, and rejected by Cloudflare only after the whole clip had uploaded.
 */
function advanceRecordingClock(): void {
    elapsed.value = (performance.now() - takeStartedAt) / 1000;

    if (elapsed.value >= props.clipSeconds) {
        finishRecording();
    }
}

async function finishRecording(): Promise<void> {
    // A second Stop before recorder.stop() resolves would meet an inactive recorder, take the
    // error path, and throw away the take the first call is still collecting.
    if (finishing.value) {
        return;
    }

    finishing.value = true;
    stopTheTimer();

    try {
        takenClip = await recorder.stop();
    } catch (thrown) {
        error.value =
            thrown instanceof Error
                ? thrown.message
                : "The recording could not be completed.";
        closeRecorder();

        return;
    } finally {
        finishing.value = false;
    }

    releaseTheCamera();

    playbackUrl.value = URL.createObjectURL(takenClip);
    stage.value = "review";
}

function showTheTake(event: Event): void {
    fitTheFrameToTheMedia(event);
    giveTheTakeALength(event);
}

/**
 * Give the take a length, which the container it was recorded into does not carry.
 *
 * A live recording is written without a duration in its header, so the browser answers Infinity and
 * lays the controls out as a live stream: no length, and a scrubber that cannot be dragged back over
 * what was just recorded. Seeking past the end makes it read through to the last frame, where the
 * length actually is.
 */
function giveTheTakeALength(event: Event): void {
    const player = event.target as HTMLVideoElement;

    if (Number.isFinite(player.duration)) {
        return;
    }

    const rewindOnceItAnswers = (): void => {
        if (!Number.isFinite(player.duration)) {
            return;
        }

        player.removeEventListener("durationchange", rewindOnceItAnswers);
        player.currentTime = 0;
    };

    player.addEventListener("durationchange", rewindOnceItAnswers);
    player.currentTime = Number.MAX_SAFE_INTEGER;
}

/**
 * Throw the take away and open the camera again.
 *
 * Nothing has been uploaded by this point, deliberately: a customer who hates their first take
 * should not have paid for it in bandwidth or in Stream storage.
 */
async function retake(): Promise<void> {
    releaseTheCamera();
    resetTheTake();

    if (!(await acquireTheCamera())) {
        closeRecorder();

        return;
    }

    showTheLivePreview();
}

async function saveTheTake(): Promise<void> {
    const clip = takenClip;

    closeRecorder();

    if (!clip) {
        return;
    }

    beginUploading();

    // Only the size is checked. The recorder stopped itself at the configured length, and the
    // container it produces carries no duration to read — but a high-bitrate device can still put a
    // minute past the ceiling, and minting for a body Cloudflare will refuse costs a reservation.
    const tooLarge = reasonItIsTooLarge(clip.size);

    if (tooLarge !== null) {
        error.value = tooLarge;
        endUploading();

        return;
    }

    await sendToCloudflare(clip, "review-video");
}

function releaseTheCamera(): void {
    recorder.release();
    liveStream = null;

    if (preview.value) {
        preview.value.srcObject = null;
    }
}

function closeRecorder(): void {
    cameraGeneration++;

    stopTheTimer();
    releaseTheCamera();
    discardTheTake();
    resetTheFrame();

    recorderOpen.value = false;
    stage.value = "ready";
}

function runTimer(interval: number, onTick: () => void): void {
    stopTheTimer();

    ticker = window.setInterval(onTick, interval);
}

function stopTheTimer(): void {
    document.removeEventListener("visibilitychange", endTheTakeIfHidden);

    if (ticker !== null) {
        window.clearInterval(ticker);
        ticker = null;
    }
}

onMounted(announce);

onBeforeUnmount(() => {
    closeRecorder();
    releaseTheThumbnail();
});

defineExpose({ addFiles, openRecorder });
</script>
